/* eslint-disable @angular-eslint/component-class-suffix */
import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, Component, Input, Signal, ViewChild, computed, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { PsfAnalystModalComponent } from '@app/modal/analyst/psf-analyst-modal.component';
import { Analyst } from '@scenario/models/analyst.model';
import { Scenario } from '@scenario/models/scenario.model';
import { dispatch, select } from '@store/injectables';
import { saveSelectedScenarios } from '@store/scenario/scenarios/selected-scenarios-slice';
import { downloadReleaseDocumentation } from '@store/scenario/release-documentation/release-documentation-slice';
import { selectSubscriptionViewModel } from '@store/scenario/subscription/subscription-slice';
import { RegionsViewModel, selectRegionsViewModel } from '@store/scenario/regions/regions-slice';
import { SourcesViewModel, selectSourcesViewModel } from '@store/scenario/sources/sources-slice';
import { Subscription } from '@scenario/models/subscription.model';
import { InsightService } from '@app/services/insight.service';
import {
  UserPreferencesViewModel,
  selectUserPreferencesViewModel,
} from '@store/scenario/user-preferences/user-preferences-slice';
import { MatCheckbox } from '@angular/material/checkbox';
import { IconButtonComponent, IconComponent, ModalService } from '@totalenergiescode/tds-angular';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { PsfHourlyDataRequestModalComponent } from '@app/modal/hourly-data/psf-hourly-data-request-modal.component';
import { PsfSubscriptionModalComponent } from '@app/modal/subscription/psf-subscription-modal.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-scenarios-result-list',
    templateUrl: './scenarios-result-list.component.html',
    imports: [MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCheckbox, MatCellDef, MatCell, MatSortHeader, IconButtonComponent, IconComponent, MatMenuTrigger, MatMenu, MatMenuItem, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator, MatTooltipModule, DatePipe ]
})
export class ScenariosResultListComponent implements AfterViewInit {
  dispatch = dispatch();
  dialog = inject(MatDialog);

  @Input() public scenarios!: Scenario[];

  simpleModeDisplayedColumns: string[] = [
    'select',
    'Price Zone',
    'Source',
    'Release',
    'Scenario',
    'Baseload / Captured',
    'Actions',
  ];
  advancedModeDisplayedColumns: string[] = [
    'select',
    'Price Zone',
    'Source',
    'Release',
    'Scenario',
    'Weather Year',
    'Baseload / Captured',
    'Actions',
  ];
  dataSource = new MatTableDataSource<Scenario>();

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  selection = new SelectionModel<Scenario>(true, []);
  subscription = select(selectSubscriptionViewModel);
  regionsViewModel: Signal<RegionsViewModel> = select(selectRegionsViewModel);
  sourcesViewModel: Signal<SourcesViewModel> = select(selectSourcesViewModel);
  userPreferences: Signal<UserPreferencesViewModel> = select(selectUserPreferencesViewModel);
  advancedMode = computed(()=> this.userPreferences().userPreferences.advanced);

  subscriptions = computed(() => this.subscription().subscriptions);

  private readonly modalService = inject(ModalService);

  constructor(private readonly insightService: InsightService) {}

  ngAfterViewInit() {
    this.dataSource.data = this.scenarios;
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (scenario, property) => {
      switch (property) {
        case 'Price Zone':
          return scenario.zoneName + ' - ' + scenario.zoneAlias;
        case 'Source':
          return scenario.source;
        case 'Release':
          return scenario.sourcePublicationDate;
        case 'Scenario':
          return scenario.basecase;
        case 'Weather Year':
          return scenario.weatherYear;
        case 'Baseload / Captured':
          return scenario.subtype;
        default:
          return scenario.zoneName + ' - ' + scenario.zoneAlias;
      }
    };
  }

  onCheckChange(row: Scenario) {
    this.selection.toggle(row);
    this.dispatch(saveSelectedScenarios(this.selection.selected));
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    if( numSelected === 0 && numRows === 0){
        return false;
    }
    return numSelected === numRows;
  }

  openHourlyDataRequestModal(scenario: Scenario) {
    this.insightService.recordEvent('request_hourly_data', {});
    this.modalService.open({
      content: {
        component: PsfHourlyDataRequestModalComponent,
        inputs: {
          defaultZone: scenario.zoneAlias,
          defaultSource: scenario.source,
          defaultBasecase: scenario.basecase,
          defaultWeatherYear: scenario.weatherYear,
        },
      },
    });
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      this.dispatch(saveSelectedScenarios(this.selection.selected));
      return;
    }

    this.selection.select(...this.dataSource.data);
    this.dispatch(saveSelectedScenarios(this.selection.selected));
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: Scenario): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row `;
  }

  openDialog(analyst: Analyst) {
    this.insightService.recordEvent('chat', {});
    this.modalService.open({
      content: {
        component: PsfAnalystModalComponent,
        inputs: {
          analyst,
        }
      },
    });
  }

  subscriptionIsNotActive(scenario: Scenario): boolean {
    const zone = this.regionsViewModel().regions.find(zone => zone.alias === scenario.zoneAlias);
    const source = this.sourcesViewModel().sources.find(source => source.name === scenario.source);

    return !this.subscriptions().some(
        sub => {
          if (sub.zoneId && !sub.sourceId) {
            return sub.zoneId === zone?.id;
          } else if (!sub.zoneId && sub.sourceId) {
            return sub.sourceId === source?.id;
          } else {
            return sub.sourceId === source?.id && sub.zoneId === zone?.id;
          }
        }
    );
  }

  getSubscription(scenario: Scenario): Subscription {
    const zone = scenario.zoneAlias ? this.regionsViewModel().regions.find(zone => zone.alias === scenario.zoneAlias) : null;
    const source = scenario.source ? this.sourcesViewModel().sources.find(source => source.name === scenario.source) : null;
    return this.subscriptions().find(sub =>  sub.zoneId === zone?.id && sub.sourceId === source?.id) ??  { id: 0, sourceId: 0, zoneId: 0 };
  }

  openSubscriptionModal(scenario: Scenario) {
    this.insightService.recordEvent('subscribe', {});
    this.modalService.open({
      content: {
        component: PsfSubscriptionModalComponent,
        inputs: {
          defaultSource: scenario.source,
          defaultZone: scenario.zoneAlias,
        }
      }
    });
  }

  downloadAnalystFile(scenario: Scenario) {
    const fileUrl = scenario.pmoFileUrl;
    const originalFileName = scenario.pmoOriginalFileName;
    if (!fileUrl || !originalFileName) {
      return;
    }
    this.insightService.recordEvent('download_power_market_outlook', {
      zoneFullName: scenario.zoneName + ' - ' + scenario.zoneAlias,
      release: scenario.sourcePublicationDate,
      originalFileName: originalFileName,
    });
    this.dispatch(downloadReleaseDocumentation({ fileName: fileUrl, originalFileName: originalFileName }));
  }

  getDisplayedColumns() {
    return this.advancedMode() ? this.advancedModeDisplayedColumns : this.simpleModeDisplayedColumns;
  }
}
